import { useCallback, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDeleteMessageByIdMutation } from 'src/store/services';
import { AppRoutes, DeleteMessageRequestArgs } from 'src/types';
import { useGetUser, useMessageListRefActions } from 'src/hooks';
import { COMMON_ERROR_TEXT } from 'src/constants';
import { useConversationActions } from '../conversationHooks';
import ForwardRefContext from 'src/contexts/ForwardRefContext';

export const useDeleteMessage = () => {
  const { user_id } = useGetUser();
  const navigate = useNavigate();
  const { deleteConversationById } = useConversationActions();
  const [deleteMessageById, { isLoading: isDeleteLoading }] =
    useDeleteMessageByIdMutation();
  const { resetMessageListRefCache } = useMessageListRefActions();

  const { mainMessageListRef } = useContext(ForwardRefContext);

  const handleDeleteMessageById = useCallback(
    async (
      params: Omit<DeleteMessageRequestArgs, 'user_id' | 'mainMessageListRef'>,
    ) => {
      try {
        if (params.shouldRemoveConversation) {
          await deleteConversationById({
            conversation_id: params.conversation_id,
          });

          resetMessageListRefCache();

          navigate(AppRoutes.HOME);
          return;
        }

        await deleteMessageById({
          ...params,
          user_id,
          mainMessageListRef,
        }).unwrap();
      } catch (error) {
        toast.error(COMMON_ERROR_TEXT);
      }
    },
    [
      deleteMessageById,
      user_id,
      mainMessageListRef,
      deleteConversationById,
      navigate,
      resetMessageListRefCache,
    ],
  );

  return useMemo(
    () => ({
      isDeleteLoading,
      deleteMessageById: handleDeleteMessageById,
    }),
    [isDeleteLoading, handleDeleteMessageById],
  );
};
